/**
 * components/wizard/AddZonePanel.jsx — Phase E v8c.11 (2026-02-28).
 *
 * Side panel shown while the operator is collecting the image-pixel
 * clicks for an ADDITIONAL calibration zone (stacked on top of the
 * base rectangle). Supports all 3 preset kinds:
 *   • area  — 4-click rectangle
 *   • line  — 2-click line segment
 *   • point — 1 double-click anchor
 *
 * Each preset's `requiresSide` (or `pt0PtAxis === "width"`) flag
 * toggles a mandatory LEFT/RIGHT selector. For `brick_to_brick_rect`
 * we surface the explicit click order inline.
 *
 * 🆕 v8c.11 — Multi-Capture (append-only). The legacy v8c.3.1
 * "(preset_id, side)" slot filter that USED to remove exhausted
 * presets from the dropdown has been REMOVED. The operator is now
 * free to mark the same line / area / point at multiple timestamps
 * (e.g. capture the top sideline at t=32s during a wide shot AND
 * again at t=114s during a zoomed shot of the opposite endzone).
 * Each capture feeds an additional row of correspondences into the
 * joint multi-zone DLT — the more redundant captures, the more
 * RANSAC has to work with to reject lens distortion outliers.
 *
 * Captured timestamps for the currently-selected (preset_id, side)
 * are surfaced as compact badges below the dropdown so the operator
 * doesn't accidentally re-capture an already-marked moment while
 * scrubbing the timeline.
 */
import React from "react";
import { RotateCcw, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { presetDisplayDimsFor } from "@/lib/fieldPresets";
import { clickGuidanceFor } from "@/lib/clickGuidance";
import ClickGuidanceDiagram from "./ClickGuidanceDiagram";

// 🆕 v8c.13c (2026-02-28) · Unified clockwise (CW) click-order
// labels across the entire wizard — see Step2Calibration.jsx for
// the full rationale + math contract. Both label sets are CW from
// screen-TL and carry the ①②③④ prefix so the operator can never
// mistake their direction even on a quick scrub.
const LABELS_FULL = [
  "① Eckfahne · OBEN LINKS",
  "② Eckfahne · OBEN RECHTS",
  "③ Eckfahne · UNTEN RECHTS",
  "④ Eckfahne · UNTEN LINKS",
];
const LABELS_EZ = [
  "① Endzonen-Ecke · OBEN LINKS",
  "② Endzonen-Ecke · OBEN RECHTS",
  "③ Endzonen-Ecke · UNTEN RECHTS",
  "④ Endzonen-Ecke · UNTEN LINKS",
];

const norm = (s) => (s || "").toLowerCase() || null;

export default function AddZonePanel({
  fmt = "Outdoor", allPresets = [], basePresetId, baseSide,
  baseAtTime = 0, currentTime = null,
  extraZones = [],
  zonePresetId, setZonePresetId, zoneSide, setZoneSide,
  points, resetPoints, zoneCount,
  onCancel, onConfirm, saving,
  fieldProfile,
}) {
  // ── 🆕 v8c.11 (2026-02-28) · Multi-Capture (no slot filter) ──
  // Every preset stays available regardless of how many times it has
  // already been captured. The operator decides whether redundant
  // captures help or not — the joint DLT will overdetermine the system
  // and let RANSAC reject outliers.
  const candidatePresets = allPresets;
  const preset = zonePresetId ? candidatePresets.find((p) => p.id === zonePresetId) : null;
  const requiresSide = !!preset?.requiresSide || preset?.pt0PtAxis === "width";

  // For sided presets the side selector still must be filled but BOTH
  // left + right always remain selectable. No auto-fill anymore — the
  // operator picks intentionally so the captured side is unambiguous.
  const sidePicked = !requiresSide || !!zoneSide;

  // 🆕 v8c.4 · Required-points count per preset kind.
  const expectedPoints = (() => {
    const k = preset?.kind;
    if (k === "point") return 1;
    if (k === "line") return 2;
    return 4;
  })();
  // 🆕 v8c.13 (2026-02-28) · Hard-lock the Save button on illegal
  // point counts. Operator must click EXACTLY `expectedPoints` clicks
  // (no over-clicks, no partial / submission). The pointsExact guard
  // closes the bug class where an over-click left N+1 points in the
  // buffer but the button still submitted N (truncated polygon).
  const pointsExact = !!preset && points.length === expectedPoints;
  const pointsMissing = preset ? Math.max(0, expectedPoints - points.length) : 0;
  const pointsOverflow = preset ? Math.max(0, points.length - expectedPoints) : 0;
  const canConfirm = pointsExact && sidePicked && !!zonePresetId && !saving;

  // 🆕 v8c.13 · Explicit click-order + landmark guidance derived from
  // the preset (lib/clickGuidance.js). Eliminates the 352 px DLT
  // inversion bug that happened when operators clicked line endpoints
  // right→left on `*_goal_to_goal` presets without realising direction
  // mattered. Source-of-truth for the wording lives in the helper so
  // copy iteration doesn't touch the geometric registry. The optional
  // `diagram` descriptor (v8c.13b) drives the inline SVG micro-blueprint
  // rendered next to the textual guidance — side-aware so e.g. picking
  // the RIGHT endzone swaps the shaded box across the diagram.
  const guidance = clickGuidanceFor(preset, { side: zoneSide });

  // 🆕 v8c.11 · Build a map of (preset_id|side) → [t, t, ...] for the
  // capture-history badges. The base zone counts as one capture at
  // its own `baseAtTime` (passed from Step2Calibration's cornersTime
  // so the BASIS chip shows the REAL base frame timestamp, not "0").
  // For badge legibility we round to 1 decimal.
  const captureHistory = {};
  const baseKey = `${basePresetId}|${norm(baseSide)}`;
  captureHistory[baseKey] = [{ t: Number(baseAtTime) || 0, kind: "base" }];
  for (const z of extraZones) {
    const key = `${z.preset_id}|${norm(z.side)}`;
    if (!captureHistory[key]) captureHistory[key] = [];
    captureHistory[key].push({ t: Number(z.at_time_s) || 0, kind: "extra" });
  }
  function _captureHistoryFor(presetId, side) {
    if (!presetId) return [];
    return captureHistory[`${presetId}|${norm(side)}`] || [];
  }
  const activeHistory = _captureHistoryFor(zonePresetId, zoneSide || (!requiresSide ? null : ""));

  // For the brick-to-brick rect we render the explicit corner sequence
  // inline. Lines + Points use their own labels.
  const clickLabels = preset?.clickInstructions
    ?? (preset?.kind === "point" ? ["DoubleClick auf Brick-Marke"]
        : preset?.kind === "line" ? ["START-Punkt", "END-Punkt"]
        : (preset?.pt0PtAxis === "width" ? LABELS_EZ : LABELS_FULL));

  return (
    <div
      data-testid="cal-add-zone-panel"
      className="border border-emerald-500/40 bg-card rounded-sm p-5 space-y-4"
    >
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300 mb-1">
          Multi-Zone · Capture #{zoneCount + 1}
        </div>
        <h3 className="font-display font-black uppercase text-lg tracking-wider">
          Zusätzliche Zone
        </h3>
        <div className="font-mono text-[10px] text-muted-foreground mt-1 leading-relaxed">
          Du kannst dieselbe Linie / Fläche zu mehreren Zeitpunkten
          erfassen — jede Erfassung verstärkt die Homographie.
        </div>
      </div>

      {/* Preset picker */}
      <div>
        <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Zone-Preset
        </Label>
        <Select value={zonePresetId} onValueChange={setZonePresetId}>
          <SelectTrigger
            data-testid="cal-add-zone-preset"
            className="mt-1.5 bg-secondary border-border focus:border-primary"
          >
            <SelectValue placeholder="Wähle einen Feld-Bereich..." />
          </SelectTrigger>
          <SelectContent>
            {candidatePresets.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground italic">
                Keine Presets verfügbar.
              </div>
            ) : (() => {
              // 🆕 v8c.4 · Group by kind so the operator sees clear
              // categories: FLÄCHEN (4 clicks), LINIEN (2 clicks),
              // PUNKTE (1 doubleclick).
              const groups = {
                area:  { label: "FLÄCHEN · 4 Klicks", items: [] },
                line:  { label: "LINIEN · 2 Klicks", items: [] },
                point: { label: "PUNKTE · 1 DoppelKlick", items: [] },
              };
              candidatePresets.forEach((p) => {
                const k = p.kind || "area";
                if (groups[k]) groups[k].items.push(p);
              });
              const renderItem = (p) => {
                // 🆕 v8c.8 · Profile-aware label so a U17 endzone reads
                // "10×25 m" instead of the registry's Adult-default
                // "18×37 m". For LINE presets we render the 1D segment
                // length (e.g. "24 m" for brick→brick top); points → "—".
                const dimLabel = presetDisplayDimsFor(fmt, p.id, fieldProfile);
                // 🆕 v8c.11 · Count of previous captures (base + extras)
                // across all sides for this preset. Shown as a compact
                // "·×N" badge so the operator sees at a glance how much
                // coverage they already have.
                const sided = !!p.requiresSide || p.pt0PtAxis === "width";
                let captureCount = 0;
                if (sided) {
                  captureCount += _captureHistoryFor(p.id, "left").length;
                  captureCount += _captureHistoryFor(p.id, "right").length;
                } else {
                  captureCount += _captureHistoryFor(p.id, null).length;
                }
                return (
                  <SelectItem key={p.id} value={p.id} data-testid={`cal-add-zone-preset-${p.id}`}>
                    <div className="flex items-center justify-between gap-3 w-full">
                      <span className="font-medium">{p.labelDe}</span>
                      <span className="flex items-center gap-1.5">
                        {captureCount > 0 && (
                          <span
                            data-testid={`cal-add-zone-capture-count-${p.id}`}
                            className="font-mono text-[9px] text-emerald-300 tabular-nums px-1 rounded-sm bg-emerald-500/15 border border-emerald-500/30"
                          >
                            ×{captureCount}
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                          {dimLabel}
                        </span>
                      </span>
                    </div>
                  </SelectItem>
                );
              };
              return ["area", "line", "point"].map((kind) =>
                groups[kind].items.length > 0 && (
                  <div key={kind} className="pt-1">
                    <div className="px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                      {groups[kind].label}
                    </div>
                    {groups[kind].items.map(renderItem)}
                  </div>
                )
              );
            })()}
          </SelectContent>
        </Select>
        {preset?.hint && (
          <div className="mt-1.5 font-mono text-[10px] text-muted-foreground leading-relaxed">
            💡 {preset.hint}
          </div>
        )}
      </div>

      {/* 🆕 v8c.13 (2026-02-28) · Prominent click-order + landmark
          guidance directly below the preset selector. This is the
          single most important UX hint the operator sees during
          multi-zone capture — it locks the directional click-order
          (LINKS → RECHTS, IM UHRZEIGERSINN), names the EXACT field
          landmarks to target, and forbids partial-line captures.
          The string is sourced from `lib/clickGuidance.js` so copy
          iteration doesn't touch the geometric registry.
          🆕 v8c.13b (2026-02-28) · Inline SVG micro-blueprint on the
          right column visualises the click-order so even an operator
          working in bad light / under stress cannot mistake the
          directional convention. */}
      {guidance && (
        <div
          data-testid="cal-add-zone-guidance"
          className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-3 flex items-start gap-3"
        >
          <div className="flex-1 min-w-0">
            <div
              data-testid="cal-add-zone-guidance-headline"
              className="font-display font-black text-amber-200 text-sm leading-tight"
            >
              {guidance.headline}
            </div>
            <div
              data-testid="cal-add-zone-guidance-body"
              className="mt-1.5 text-foreground/90 text-[13px] leading-relaxed"
            >
              {guidance.body}
            </div>
          </div>
          {guidance.diagram && (
            <ClickGuidanceDiagram diagram={guidance.diagram} />
          )}
        </div>
      )}

      {/* Left/Right side selector — only for presets that need one.
          v8c.11: BOTH sides always remain selectable; the operator
          picks intentionally each time so the captured side is
          unambiguous even when multi-capturing the same line. */}
      {requiresSide && preset && (
        <div className="border border-amber-400/30 bg-amber-400/5 rounded-sm p-3">
          <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
            Feld-Seite · Pflichtfeld
          </Label>
          <Select value={zoneSide} onValueChange={setZoneSide}>
            <SelectTrigger
              data-testid="cal-add-zone-side"
              className={`mt-1.5 bg-secondary border-border focus:border-primary ${sidePicked ? "" : "ring-1 ring-amber-400/60"}`}
            >
              <SelectValue placeholder="Links oder Rechts wählen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left" data-testid="cal-add-zone-side-left">Linke Hälfte</SelectItem>
              <SelectItem value="right" data-testid="cal-add-zone-side-right">Rechte Hälfte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* 🆕 v8c.11 · Capture-history badges for the active preset/side.
          Lets the operator confirm "did I already capture this at
          t=32s?" without scrolling through the extra-zones list. */}
      {preset && activeHistory.length > 0 && (
        <div
          data-testid="cal-add-zone-history"
          className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3"
        >
          <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-300 mb-1.5">
            Bereits erfasst · {preset.labelDe}
            {zoneSide && (
              <span className="ml-1 text-muted-foreground">
                ({zoneSide === "left" ? "Links" : "Rechts"})
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activeHistory.map((h, i) => (
              <span
                key={i}
                data-testid={`cal-add-zone-history-badge-${i}`}
                className={`font-mono text-[10px] tabular-nums px-1.5 py-0.5 rounded-sm border ${
                  h.kind === "base"
                    ? "bg-amber-500/10 border-amber-400/40 text-amber-300"
                    : "bg-emerald-500/10 border-emerald-400/40 text-emerald-300"
                }`}
                title={h.kind === "base" ? "Basis-Zone" : "Zusätzliche Erfassung"}
              >
                {h.kind === "base" ? `BASIS · t=${h.t.toFixed(1)}s` : `t=${h.t.toFixed(1)}s`}
              </span>
            ))}
          </div>
          {Number.isFinite(currentTime) && (
            <div
              data-testid="cal-add-zone-current-time"
              className="mt-2 font-mono text-[9px] tracking-widest text-emerald-300"
            >
              ▶ AKTUELLER FRAME · t={Number(currentTime).toFixed(1)}s
              <span className="text-muted-foreground ml-1.5">
                (wird beim Speichern als Capture-Timestamp übernommen)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Click instructions (custom order for brick_to_brick_rect; canonical otherwise) */}
      {preset && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Klick-Reihenfolge
          </div>
          <ol className="space-y-1.5">
            {clickLabels.map((l, i) => {
              const set = !!points[i];
              return (
                <li key={l} className="flex items-center gap-3 text-sm">
                  <span className={`w-5 h-5 rounded-sm border flex items-center justify-center font-mono text-[10px] ${
                    set ? "bg-emerald-400 text-black border-emerald-400"
                        : "bg-secondary border-border text-muted-foreground"
                  }`}>{i + 1}</span>
                  <span className={set ? "text-foreground" : "text-muted-foreground"}>{l}</span>
                  {set && (
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                      {Math.round(points[i].x)}, {Math.round(points[i].y)}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* 🆕 v8c.13 (2026-02-28) · Explicit click-count status + hard
          lock on illegal counts. Without this guard a partially-
          completed polygon (e.g. 2 of 4 endzone corners) used to slip
          through when the operator misjudged the count badge. */}
      {preset && (
        <div
          data-testid="cal-add-zone-point-status"
          className={`border rounded-sm p-2.5 font-mono text-[11px] tracking-wider ${
            pointsExact
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
              : pointsOverflow > 0
                ? "border-rose-500/50 bg-rose-500/10 text-rose-300"
                : "border-amber-500/40 bg-amber-500/5 text-amber-300"
          }`}
        >
          <span className="font-display font-bold uppercase tracking-widest mr-2">
            Klick-Status:
          </span>
          <span className="tabular-nums">
            {points.length}/{expectedPoints} gesetzt
          </span>
          {pointsExact && (
            <span className="ml-2 text-emerald-200">
              ✓ vollständig — bereit zum Speichern
            </span>
          )}
          {pointsMissing > 0 && (
            <span data-testid="cal-add-zone-point-missing" className="ml-2">
              · noch {pointsMissing} Klick{pointsMissing === 1 ? "" : "s"} fehlen
            </span>
          )}
          {pointsOverflow > 0 && (
            <span data-testid="cal-add-zone-point-overflow" className="ml-2">
              · {pointsOverflow} zu viele — bitte Reset klicken
            </span>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button data-testid="cal-add-zone-reset" variant="outline" onClick={resetPoints}
          className="flex-1 border-border bg-secondary hover:bg-accent text-foreground">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset
        </Button>
        <Button data-testid="cal-add-zone-cancel" variant="outline" onClick={onCancel}
          className="flex-1 border-rose-400/40 bg-rose-400/5 text-rose-300 hover:bg-rose-400 hover:text-black font-display font-bold uppercase tracking-widest">
          <X className="w-4 h-4 mr-2" /> Abbrechen
        </Button>
        <Button data-testid="cal-add-zone-confirm" onClick={onConfirm} disabled={!canConfirm}
          className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-display font-bold uppercase tracking-widest disabled:opacity-40">
          <Check className="w-4 h-4 mr-2" /> Zone speichern
        </Button>
      </div>
    </div>
  );
}
